import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Modal } from 'react-bootstrap';
import { FiCloudRain, FiRefreshCw, FiEye, FiTrash2, FiCheckCircle, FiSliders } from 'react-icons/fi';
import { fetchActivityForecastsRequest } from '../../store/actions/forecastsActions';
import { createCheckClaimFromForecast, deleteActivityForecast } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsForecasts.css';

const EMPTY_ACTIVITY_FORECASTS_FILTERS = {
    policy_code: '',
    activity_start_date: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
};

const getDeep = (obj, path, fallback = 'N/A') => {
    try {
        const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
        return val ?? fallback;
    } catch {
        return fallback;
    }
};

const AdminActivityForecasts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const forecastsState = useSelector((state) => state.forecasts.activityForecasts);
    const { t } = useTranslation();

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('activity-forecasts', EMPTY_ACTIVITY_FORECASTS_FILTERS, {
        persistWhen: isAuthenticated,
    });
    const [processingForecastId, setProcessingForecastId] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedForecast, setSelectedForecast] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [forecastToDelete, setForecastToDelete] = useState(null);
    const [deletingForecastId, setDeletingForecastId] = useState(null);

    const handleOpenConfirmModal = (item) => {
        setSelectedForecast(item);
        setShowConfirmModal(true);
    };

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
        setSelectedForecast(null);
    };

    const handleCreateCheckClaim = async () => {
        if (!selectedForecast) {
            return;
        }

        const forecastId = selectedForecast.forecast_id;
        if (!forecastId) {
            setErrorMessage('ID de prévision manquant');
            handleCloseConfirmModal();
            return;
        }

        try {
            setProcessingForecastId(forecastId);
            setErrorMessage(null);
            setSuccessMessage(null);
            handleCloseConfirmModal();

            await createCheckClaimFromForecast(forecastId);

            const policyCode = getDeep(selectedForecast, ['policy_info', 'policy_code'], '');
            setSuccessMessage(
                `Check claim créé avec succès pour la prévision ${policyCode ? `(Police: ${policyCode})` : forecastId}`
            );
            // Recharger les prévisions après un court délai
            setTimeout(() => {
                loadForecasts(currentPage);
                setSuccessMessage(null);
            }, 3000);
        } catch (error) {
            let errorMsg = 'Erreur lors de la création du check claim';

            // Extraire le message d'erreur du backend
            if (error.response?.data) {
                const responseData = error.response.data;

                // Gérer le cas spécifique où un check claim existe déjà
                if (error.response.status === 400 && responseData.detail) {
                    const detail = responseData.detail;
                    if (typeof detail === 'string' && detail.includes('already exists')) {
                        // Formater le message pour qu'il soit plus convivial
                        const forecastIdMatch = detail.match(/forecast_id\s+([a-f0-9-]+)/i);
                        const policyCode = getDeep(selectedForecast, ['policy_info', 'policy_code'], '');
                        if (forecastIdMatch) {
                            errorMsg = `Un check claim existe déjà pour cette prévision${policyCode ? ` (Police: ${policyCode})` : ''}.`;
                        } else {
                            errorMsg = 'Un check claim existe déjà pour cette prévision.';
                        }
                    } else if (typeof detail === 'string') {
                        errorMsg = detail;
                    } else {
                        errorMsg = handleApiError(error, errorMsg);
                    }
                } else {
                    errorMsg = handleApiError(error, errorMsg);
                }
            } else if (error.message) {
                errorMsg = error.message;
            } else {
                errorMsg = handleApiError(error, errorMsg);
            }

            setErrorMessage(errorMsg);
            setTimeout(() => setErrorMessage(null), 6000);
        } finally {
            setProcessingForecastId(null);
        }
    };

    const buildManualForecastCheckState = (item) => (
        item
            ? {
                policy_code: getDeep(item, ['policy_info', 'policy_code'], ''),
                reservation_number: getDeep(item, ['activity_info', 'reservation_number'], ''),
                activity_start_date: getDeep(item, ['activity_info', 'activity_start_date'], ''),
                activity_end_date: getDeep(item, ['activity_info', 'activity_end_date'], ''),
                activity_start_time: getDeep(item, ['activity_info', 'activity_start_time'], ''),
                activity_end_time: getDeep(item, ['activity_info', 'activity_end_time'], ''),
            }
            : {}
    );

    const isGreenRiverPartner = (item) => {
        const companyName = String(getDeep(item, ['partner_info', 'company_name'], '')).toLowerCase();
        return companyName.includes('green river');
    };

    const handleManualForecastCheck = (item = null) => {
        navigate('/activity/forecasts/manual-check', { state: buildManualForecastCheckState(item) });
    };

    const handleGreenRiverManualForecastCheck = (item = null) => {
        navigate('/activity/forecasts/manual-check/green-river', { state: buildManualForecastCheckState(item) });
    };

    const handleDetails = (item) => {
        const forecastId = item.forecast_id;
        if (forecastId) {
            navigate(`/activity/forecasts/${forecastId}`, {
                state: { forecast: item }
            });
        } else {
            console.log('Details activity forecast:', item);
            alert(`Détails forecast pour police ${getDeep(item, ['policy_info', 'policy_code'], '')}`);
        }
    };

    const handleOpenDeleteModal = (item) => {
        setForecastToDelete(item);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setForecastToDelete(null);
    };

    const handleDelete = async () => {
        if (!forecastToDelete) {
            return;
        }

        const forecastId = forecastToDelete.forecast_id;
        if (!forecastId) {
            setErrorMessage('ID de prévision manquant');
            handleCloseDeleteModal();
            return;
        }

        try {
            setDeletingForecastId(forecastId);
            setErrorMessage(null);
            setSuccessMessage(null);
            handleCloseDeleteModal();

            await deleteActivityForecast(forecastId);

            const policyCode = getDeep(forecastToDelete, ['policy_info', 'policy_code'], '');
            setSuccessMessage(
                `Prévision supprimée avec succès${policyCode ? ` (Police: ${policyCode})` : ''}`
            );
            // Recharger les prévisions après un court délai
            setTimeout(() => {
                loadForecasts(currentPage);
                setSuccessMessage(null);
            }, 2000);
        } catch (error) {
            let errorMsg = 'Erreur lors de la suppression de la prévision';

            // Extraire le message d'erreur du backend
            if (error.response?.data) {
                const responseData = error.response.data;

                // Gérer les erreurs d'intégrité de base de données
                if (error.response.status === 400 && responseData.detail) {
                    const detail = responseData.detail;

                    // Si c'est un objet avec un code d'erreur INTEGRITY_ERROR
                    if (typeof detail === 'object' && detail.code === 'INTEGRITY_ERROR') {
                        if (detail.message && detail.message.includes('integrity constraint')) {
                            errorMsg = 'Impossible de supprimer cette prévision car elle est liée à un ou plusieurs check claims. Veuillez d\'abord supprimer les check claims associés.';
                        } else if (detail.message) {
                            errorMsg = detail.message;
                        } else {
                            errorMsg = 'Erreur d\'intégrité : cette prévision ne peut pas être supprimée car elle est référencée ailleurs.';
                        }
                    }
                    // Si detail est une chaîne
                    else if (typeof detail === 'string') {
                        if (detail.includes('integrity constraint') || detail.includes('violates foreign key')) {
                            errorMsg = 'Impossible de supprimer cette prévision car elle est liée à d\'autres données.';
                        } else {
                            errorMsg = detail;
                        }
                    }
                    // Si detail est un objet avec un message
                    else if (detail.message) {
                        errorMsg = detail.message;
                    } else {
                        errorMsg = handleApiError(error, errorMsg);
                    }
                } else {
                    errorMsg = handleApiError(error, errorMsg);
                }
            } else if (error.message) {
                errorMsg = error.message;
            } else {
                errorMsg = handleApiError(error, errorMsg);
            }

            setErrorMessage(errorMsg);
            setTimeout(() => setErrorMessage(null), 8000);
        } finally {
            setDeletingForecastId(null);
        }
    };

    // Charger les prévisions avec pagination et filtres
    const loadForecasts = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                params[key] = activeFilters[key].trim();
            }
        });

        dispatch(fetchActivityForecastsRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadForecasts(currentPage);
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        loadForecasts(currentPage);
    }, [currentPage]);

    // Extraire les données et métadonnées de la réponse API
    const forecastsData = forecastsState?.data?.data || forecastsState?.data?.forecast_details || forecastsState?.data?.policies_with_forecasts || forecastsState?.data?.forecasts || forecastsState?.data || [];
    const forecastsArray = Array.isArray(forecastsData) ? forecastsData : [];
    const meta = forecastsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + forecastsArray.length, totalItems);

    const handleRefresh = () => {
        loadForecasts(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadForecasts(1, filters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_ACTIVITY_FORECASTS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadForecasts(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les prévisions de l'API (pas de filtrage côté client)
    const currentForecasts = forecastsArray;

    return (
        <div className="dashboard-page admin-trips-forecasts-page">
            <Card>
                <Card.Body>
                    <div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                            <h2 className="page-title">
                                <FiCloudRain className="me-2" />
                                {t('pages.activityForecasts.title')}
                            </h2>
                            <p className="text-muted mb-0">{t('pages.activityForecasts.subtitle')}</p>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <Button
                                variant="primary"
                                onClick={() => handleManualForecastCheck()}
                                className="d-flex align-items-center"
                            >
                                <FiSliders className="me-2" />
                                {t('pages.activityForecasts.manualCheck.button')}
                            </Button>
                            <Button
                                variant="success"
                                onClick={() => handleGreenRiverManualForecastCheck()}
                                className="d-flex align-items-center"
                            >
                                <FiSliders className="me-2" />
                                {t('pages.activityForecasts.greenRiverManualCheck.button')}
                            </Button>
                        </div>
                    </div>

                    {forecastsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {forecastsState.error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert variant="success" className="mt-3" onClose={() => setSuccessMessage(null)} dismissible>
                            <strong>Succès !</strong> {successMessage}
                        </Alert>
                    )}

                    {errorMessage && (
                        <Alert variant="danger" className="mt-3" onClose={() => setErrorMessage(null)} dismissible>
                            <strong>Erreur !</strong> {errorMessage}
                        </Alert>
                    )}

                    {/* Modal de confirmation */}
                    <Modal show={showConfirmModal} onHide={handleCloseConfirmModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiCheckCircle className="me-2" />
                                Confirmer la création du check claim
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Êtes-vous sûr de vouloir créer un check claim pour cette prévision ?</p>
                            {selectedForecast && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    <p className="mb-1"><strong>Code police :</strong> {getDeep(selectedForecast, ['policy_info', 'policy_code'], '—')}</p>
                                    <p className="mb-1"><strong>Date prévision :</strong> {getDeep(selectedForecast, ['forecast_date'], '—')}</p>
                                    <p className="mb-1"><strong>Ville :</strong> {getDeep(selectedForecast, ['activity_info', 'activity_city'], '—')}</p>
                                    <p className="mb-0"><strong>Total précipitation :</strong> {getDeep(selectedForecast, ['total_mm_prc'], '0')} mm</p>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseConfirmModal}>
                                Annuler
                            </Button>
                            <Button
                                variant="success"
                                onClick={handleCreateCheckClaim}
                                disabled={processingForecastId === getDeep(selectedForecast, ['forecast_id'])}
                            >
                                {processingForecastId === getDeep(selectedForecast, ['forecast_id']) ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Création en cours...
                                    </>
                                ) : (
                                    <>
                                        <FiCheckCircle className="me-2" />
                                        Confirmer
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiTrash2 className="me-2" />
                                Confirmer la suppression
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Êtes-vous sûr de vouloir supprimer cette prévision ? Cette action est irréversible.</p>
                            {forecastToDelete && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    <p className="mb-1"><strong>Code police :</strong> {getDeep(forecastToDelete, ['policy_info', 'policy_code'], '—')}</p>
                                    <p className="mb-1"><strong>Date prévision :</strong> {getDeep(forecastToDelete, ['forecast_date'], '—')}</p>
                                    <p className="mb-1"><strong>Ville :</strong> {getDeep(forecastToDelete, ['activity_info', 'activity_city'], '—')}</p>
                                    <p className="mb-0"><strong>Total précipitation :</strong> {getDeep(forecastToDelete, ['total_mm_prc'], '0')} mm</p>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseDeleteModal}>
                                Annuler
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                disabled={deletingForecastId === getDeep(forecastToDelete, ['forecast_id'])}
                            >
                                {deletingForecastId === getDeep(forecastToDelete, ['forecast_id']) ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Suppression en cours...
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        Supprimer
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 301826051"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_first_name"
                                            value={filters.client_first_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Prénom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_last_name"
                                            value={filters.client_last_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date"
                                            value={filters.activity_start_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={forecastsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${forecastsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {forecastsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des prévisions (activities)...</p>
                        </div>
                    ) : forecastsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{forecastsState.error}</Alert>
                        </div>
                    ) : currentForecasts.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCloudRain size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune prévision disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code police</th>
                                            <th>Numéro réservation</th>
                                            <th>Client</th>
                                            <th>Partenaire</th>
                                            <th>Date prévision</th>
                                            <th>Ville activité</th>
                                            <th>Pays activité</th>
                                            <th>Début activité</th>
                                            <th>Fin activité</th>
                                            <th>Type garantie</th>
                                            <th>Total mm précipitation</th>
                                            <th>Source données</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentForecasts) && currentForecasts.length > 0 ? (
                                            currentForecasts.map((item, idx) => {
                                                const clientInfo = item.client_info || {};
                                                const partnerInfo = item.partner_info || {};
                                                const clientName = `${getDeep(clientInfo, ['first_name'], '')} ${getDeep(clientInfo, ['last_name'], '')}`.trim() || '—';
                                                const partnerName = getDeep(partnerInfo, ['company_name'], getDeep(partnerInfo, ['partner_first_name'], '') + ' ' + getDeep(partnerInfo, ['partner_last_name'], '')).trim() || '—';
                                                return (
                                                    <tr key={getDeep(item, ['forecast_id'], getDeep(item, ['policy_info', 'policy_id'], idx))}>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(item, ['policy_info', 'policy_code'])}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(item, ['activity_info', 'reservation_number'])}</td>
                                                        <td>{clientName}</td>
                                                        <td>{partnerName}</td>
                                                        <td>{getDeep(item, ['forecast_date'])}</td>
                                                        <td>{getDeep(item, ['activity_info', 'activity_city'])}</td>
                                                        <td>{getDeep(item, ['activity_info', 'activity_country'])}</td>
                                                        <td>{getDeep(item, ['activity_info', 'activity_start_date'])}</td>
                                                        <td>{getDeep(item, ['activity_info', 'activity_end_date'])}</td>
                                                        <td><strong>{getDeep(item, ['total_mm_prc'], '0')} mm</strong></td>
                                                        <td>{getDeep(item, ['data_source'])}</td>
                                                        <td className="text-end col-actions">
                                                            <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleDetails(item)} title="Détails" aria-label="Détails">
                                                                <FiEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleManualForecastCheck(item)}
                                                                title={t('pages.activityForecasts.manualCheck.rowButton')}
                                                                aria-label={t('pages.activityForecasts.manualCheck.rowButton')}
                                                            >
                                                                <FiSliders />
                                                            </Button>
                                                            {isGreenRiverPartner(item) && (
                                                                <Button
                                                                    variant="outline-success"
                                                                    size="sm"
                                                                    className="me-2"
                                                                    onClick={() => handleGreenRiverManualForecastCheck(item)}
                                                                    title={t('pages.activityForecasts.greenRiverManualCheck.rowButton')}
                                                                    aria-label={t('pages.activityForecasts.greenRiverManualCheck.rowButton')}
                                                                >
                                                                    <FiSliders />
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleOpenDeleteModal(item)}
                                                                title="Supprimer"
                                                                aria-label="Supprimer"
                                                                disabled={deletingForecastId === getDeep(item, ['forecast_id'])}
                                                            >
                                                                {deletingForecastId === getDeep(item, ['forecast_id']) ? (
                                                                    <Spinner animation="border" size="sm" />
                                                                ) : (
                                                                    <FiTrash2 />
                                                                )}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="13" className="text-center">
                                                    Aucune prévision trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} prévision{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadForecasts(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadForecasts(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadForecasts(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadForecasts(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadForecasts(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadForecasts(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadForecasts(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityForecasts;

